import { CargoOperationTypeEnum } from '../enums/cargoOperationTypeEnum';
import { TankersEvent } from '../types/ViewModel/TankersEvent';
import { EventDate } from '../types/ViewModel/EventDate';
import { EventType } from '../types/ViewModel/EventType';
import { EventTypeInputReg } from '../types/ViewModel/EventTypeInputReg';
import EventTypeInputRegFactory from './EventTypeInputRegFactory';
import { EventDateFactory } from './EventDateFactory';
import { TankersEventDataBM } from '../types/BusinessModel/TankersEventDataBM';
import { EventBM } from '../types/BusinessModel/EventBM';
import useEventType from '../app/useEventType';
import { transform } from '../pipe/eventSortPipe';
import { RotationTreeEnum } from '../enums/rotationTreeEnum';
import { ReportLocationEnum } from '../enums/reportLocationEnum';
import { FrontendVersionEnum } from '../enums/frontendVersionEnum';

export default () => {
    const { getEventTypeFromEvent } = useEventType();
    const { newEventTypeInputRegArray, createNewEventTypeInputRegArray, createEventTypeInputRegBMList } = EventTypeInputRegFactory();

    const eventDateFactory = new EventDateFactory();

    const newEventList = (tankersEventData: TankersEventDataBM, offlineReportEvents: any[], isSuperShallow: boolean): TankersEvent[] => {
        const eventArray: TankersEvent[] = [];
        tankersEventData = addOfflineDataToEventList(tankersEventData, offlineReportEvents);
        const eventsAny = getEventAnyFromTanksEventData(tankersEventData);
        if (eventsAny && eventsAny.length > 0) {
            eventsAny.map((ev) => {
                if (isSuperShallow) {
                    eventArray.push(createSuperShallowEvent(ev));
                } else {
                    eventArray.push(newTankersEvent(ev));
                }
            });
        }
        let eventsMinusUnluckyPlannedEvents = eventArray.filter((ev) => ev.eventDate.actualDateTimeUtc);
        eventsMinusUnluckyPlannedEvents = transform(eventsMinusUnluckyPlannedEvents, false);
        return eventsMinusUnluckyPlannedEvents;
    };

    const addOfflineDataToEventList = (tankersEventData: TankersEventDataBM, offlineReportEvents: any[]) => {
        offlineReportEvents.forEach((offlineReportEvent) => {
            if (+offlineReportEvent.imoNumber === tankersEventData.imoNumber) {
                if (offlineReportEvent.createEventRequest) {
                    const indexOfCreatedEvent = tankersEventData.events.findIndex(
                        (e) => e.eventId === offlineReportEvent.createEventRequest.response.eventId
                    );
                    if (indexOfCreatedEvent === -1) {
                        // Only push when the server payload doesn't already contain this event.
                        // If it's already present (e.g. a SignalR push arrived before the offline
                        // queue entry was cleared), keep the freshly-fetched server copy rather than
                        // overwriting it with the (potentially stale) locally-cached create response.
                        tankersEventData.events.push(offlineReportEvent.createEventRequest.response);
                    }
                } else if (offlineReportEvent.updateEventRequest) {
                    const indexOfEvent = tankersEventData.events.findIndex(
                        (e) => e.eventId === offlineReportEvent.updateEventRequest.response.eventId
                    );
                    if (indexOfEvent !== -1) {
                        tankersEventData.events[indexOfEvent] = offlineReportEvent.updateEventRequest.response;
                    }
                } else if (offlineReportEvent.deleteEventRequest) {
                    const indexOfEvent = tankersEventData.events.findIndex((e) => e.eventId === offlineReportEvent.eventId);
                    if (indexOfEvent !== -1) {
                        tankersEventData.events.splice(indexOfEvent, 1);
                    }
                }
            }
        });

        return tankersEventData;
    };

    const getEventAnyFromTanksEventData = (tankersEventData: TankersEventDataBM) => {
        if (tankersEventData && tankersEventData.events) {
            return tankersEventData.events;
        } else {
            return null;
        }
    };

    const getLastUpdatedForEventListAnyOnly = (tankersEventData: TankersEventDataBM) => {
        if (tankersEventData && tankersEventData.lastUpdatedDateUtc) {
            return new Date(tankersEventData.lastUpdatedDateUtc);
        }
        return null;
    };

    const updateEventTypeLevel = (event: EventBM): RotationTreeEnum => {
        if (event.eventTypeContextId === ReportLocationEnum.Sea) return RotationTreeEnum.Sea;
        if (event.cargoOperationId) return RotationTreeEnum.CargoOperation;
        if (event.berthCallId) return RotationTreeEnum.BerthCall;
        return RotationTreeEnum.PortCall;
    };

    const newTankersEvent = (event: EventBM): TankersEvent => {
        let lastUpdated: Date | null = null;
        let created: Date | null = null;
        if (event.lastUpdated) {
            lastUpdated = new Date(event.lastUpdated);
        }
        if (event.created) {
            created = new Date(event.created);
        }

        const newEvent = newItem(
            event.eventId,
            event.eventTypeGroupSequenceIdentity,
            event.eventTypeGroupSequenceDependencyIdentity,
            event.eventTypeId,
            event.dependencyDisplayName,
            event.shipId,
            event.portCallId,
            event.berthCallId,
            event.berthId,
            event.berthSequenceNumber,
            event.cargoOperationId,
            event.delayEventFromId,
            event.delayEventToId,
            event.concurrentSequenceNumber,
            eventDateFactory.newEventDate(event.eventDate),
            created,
            lastUpdated,
            event.updatedBy,
            event.deleted,
            newEventTypeInputRegArray(event.eventTypeInputRegList),
            updateEventTypeLevel(event),
            event.portId,
            event.voyageNumber,
            event.voyageNumber1,
            event.voyageNumber2,
            event.cargoNumber,
            event.cargoId,
            event.imosCargoId,
            event.operationType,
            event.weight,
            event.tradeName,
            event.eventTypeDataVersion,
            event.eventTypeContextId,
            event.frontendVersion,
            event.portName,
            event.portClusterInitialPortId,
            event.portClusterInitialPortName,
            event.berthName,
            false
        );
        newEvent.eventType = getEventTypeFromEvent(newEvent);
        return newEvent;
    };

    const newItem = (
        eventId: string,
        eventTypeGroupSequenceIdentity: string,
        eventTypeGroupSequenceDependencyIdentity: string,
        eventTypeId: number,
        dependencyDisplayName: string,
        shipId: number,
        portCallId: number,
        berthCallId: number,
        berthId: number,
        berthSequenceNumber: number,
        cargoOperationId: number,
        delayEventFromId: string,
        delayEventToId: string,
        concurrentSequenceNumber: number,
        eventDate: EventDate,
        created: Date | null,
        lastUpdated: Date | null,
        updatedBy: string,
        deleted: boolean,
        eventTypeInputRegList: EventTypeInputReg[],
        eventLevel: RotationTreeEnum,
        portId: number,
        voyageNumber: string | null,
        voyageNumber1: number | null,
        voyageNumber2: number | null,
        cargoNumber: string,
        cargoId: number,
        imosCargoId: number,
        operationType: CargoOperationTypeEnum,
        weight: number,
        tradeName: string,
        eventTypeDataVersion: string,
        eventTypeContextId: number,
        frontendVersion: number,
        portName: string | null,
        portClusterInitialPortId: number | null,
        portClusterInitialPortName: string | null,
        berthName: string | null,
        delayExceeded: boolean
    ): TankersEvent => {
        const event: TankersEvent = new TankersEvent(
            eventId,
            eventTypeGroupSequenceIdentity,
            eventTypeGroupSequenceDependencyIdentity,
            eventTypeId,
            dependencyDisplayName,
            shipId,
            portCallId,
            berthCallId,
            berthId,
            berthSequenceNumber,
            cargoOperationId,
            delayEventFromId,
            delayEventToId,
            concurrentSequenceNumber,
            eventDateFactory.createEventDateFromCode(eventDate),
            created,
            lastUpdated,
            updatedBy,
            deleted,
            eventTypeInputRegList,
            eventLevel,
            portId,
            voyageNumber,
            voyageNumber1,
            voyageNumber2,
            cargoNumber,
            cargoId,
            imosCargoId,
            operationType,
            weight,
            tradeName,
            eventTypeDataVersion,
            eventTypeContextId,
            frontendVersion,
            portName,
            portClusterInitialPortId,
            portClusterInitialPortName,
            berthName,
            delayExceeded
        );
        event.eventType = getEventTypeFromEvent(event);
        return event;
    };

    const createNewEventFromCode = (
        eventId: string | null,
        eventTypeGroupSequenceIdentity: string | null,
        eventTypeGroupSequenceDependencyIdentity: string | null,
        eventTypeId: number | null,
        dependencyDisplayName: string | null,
        shipId: number,
        portCallId: number | null,
        berthCallId: number | null,
        berthId: number | null,
        berthSequenceNumber: number | null,
        cargoOperationId: number | null,
        delayEventFromId: string | null,
        delayEventToId: string | null,
        concurrentSequenceNumber: number | null,
        eventDate: EventDate,
        created: Date | null,
        lastUpdated: Date | null,
        updatedBy: string | null,
        deleted: boolean | null,
        eventTypeInputRegList: EventTypeInputReg[] | null,
        eventLevel: RotationTreeEnum | null,
        portId: number | null,
        voyageNumber: string | null,
        voyageNumber1: number | null,
        voyageNumber2: number | null,
        cargoNumber: string | null,
        cargoId: number | null,
        imosCargoId: number | null,
        operationType: CargoOperationTypeEnum | null,
        weight: number | null,
        tradeName: string | null,
        eventTypeDataVersion: string | null,
        eventTypeContextId: number | null,
        frontendVersion: number,
        eventType: EventType | null,
        portName: string | null,
        portClusterInitialPortId: number | null,
        portClusterInitialPortName: string | null,
        berthName: string | null,
        delayExceeded: boolean
    ): TankersEvent => {
        const event: TankersEvent = new TankersEvent(
            eventId,
            eventTypeGroupSequenceIdentity,
            eventTypeGroupSequenceDependencyIdentity,
            eventTypeId,
            dependencyDisplayName,
            shipId,
            portCallId,
            berthCallId,
            berthId,
            berthSequenceNumber,
            cargoOperationId,
            delayEventFromId,
            delayEventToId,
            concurrentSequenceNumber,
            eventDateFactory.createEventDateFromCode(eventDate),
            created,
            lastUpdated,
            updatedBy,
            deleted,
            createNewEventTypeInputRegArray(eventTypeInputRegList),
            eventLevel,
            portId,
            voyageNumber,
            voyageNumber1,
            voyageNumber2,
            cargoNumber,
            cargoId,
            imosCargoId,
            operationType,
            weight,
            tradeName,
            eventTypeDataVersion,
            eventTypeContextId,
            frontendVersion,
            portName,
            portClusterInitialPortId,
            portClusterInitialPortName,
            berthName,
            delayExceeded
        );
        event.eventType = eventType;

        return event;
    };

    const createNewEventList = (eventList: TankersEvent[]): TankersEvent[] => {
        return eventList.map((ev) => createNewEvent(ev));
    };

    const createNewEvent = (event: TankersEvent): TankersEvent => {
        const newEvent: TankersEvent = new TankersEvent(
            event.eventId,
            event.eventTypeGroupSequenceIdentity,
            event.eventTypeGroupSequenceDependencyIdentity,
            event.eventTypeId,
            event.dependencyDisplayName,
            event.shipId,
            event.portCallId,
            event.berthCallId,
            event.berthId,
            event.berthSequenceNumber,
            event.cargoOperationId,
            event.delayEventFromId,
            event.delayEventToId,
            event.concurrentSequenceNumber,
            eventDateFactory.createEventDateFromCode(event.eventDate),
            event.created,
            event.lastUpdated,
            event.updatedBy,
            event.deleted,
            createNewEventTypeInputRegArray(event.eventTypeInputRegList),
            event.eventLevel,
            event.portId,
            event.voyageNumber,
            event.voyageNumber1,
            event.voyageNumber2,
            event.cargoNumber,
            event.cargoId,
            event.imosCargoId,
            event.operationType,
            event.weight,
            event.tradeName,
            event.eventTypeDataVersion,
            event.eventTypeContextId,
            event.frontendVersion,
            event.portName,
            event.portClusterInitialPortId,
            event.portClusterInitialPortName,
            event.berthName,
            event.delayExceeded
        );
        newEvent.eventType = event.eventType;
        return newEvent;
    };

    const createSuperShallowEvent = (eventsAny: EventBM): TankersEvent => {
        let lastUpdated: Date | null = null;

        if (eventsAny.lastUpdated) {
            lastUpdated = new Date(eventsAny.lastUpdated);
        }
        return new TankersEvent(
            null,
            null,
            null,
            null,
            null,
            eventsAny.shipId,
            null,
            null,
            null,
            null,
            null,
            null,
            null,
            null,
            eventDateFactory.newEventDate(eventsAny.eventDate),
            null,
            lastUpdated,
            null,
            null,
            null,
            null,
            null,
            null,
            null,
            null,
            null,
            null,
            null,
            null,
            null,
            null,
            null,
            null,
            FrontendVersionEnum.OrcaWeb,
            null,
            null,
            null,
            null,
            false
        );
    };

    const createTankersEventBM = (item: TankersEvent): any => {
        return {
            eventId: item.eventId,
            eventTypeGroupSequenceIdentity: item.eventTypeGroupSequenceIdentity,
            eventTypeGroupSequenceDependencyIdentity: item.eventTypeGroupSequenceDependencyIdentity,
            eventTypeId: item.eventTypeId,
            dependencyDisplayName: item.dependencyDisplayName,
            shipId: item.shipId,
            portCallId: item.portCallId,
            berthCallId: item.berthCallId,
            berthId: item.berthId,
            berthSequenceNumber: item.berthSequenceNumber,
            cargoOperationId: item.cargoOperationId,
            delayEventFromId: item.delayEventFromId,
            delayEventToId: item.delayEventToId,
            concurrentSequenceNumber: item.concurrentSequenceNumber,
            eventDate: eventDateFactory.createEventDateBM(item.eventDate),
            created: item.created,
            lastUpdated: item.lastUpdated,
            updatedBy: item.updatedBy,
            deleted: item.deleted,
            eventTypeInputRegList: createEventTypeInputRegBMList(item.eventTypeInputRegList),
            eventLevel: item.eventLevel,
            portId: item.portId,
            voyageNumber: item.voyageNumber,
            voyageNumber1: item.voyageNumber1,
            voyageNumber2: item.voyageNumber2,
            cargoNumber: item.cargoNumber,
            cargoId: item.cargoId,
            imosCargoId: item.imosCargoId,
            operationType: item.operationType,
            weight: item.weight,
            tradeName: item.tradeName,
            eventTypeDataVersion: item.eventTypeDataVersion,
            eventTypeContextId: item.eventTypeContextId,
            frontendVersion: item.frontendVersion,
            portName: item.portName,
            portClusterInitialPortId: item.portClusterInitialPortId,
            portClusterInitialPortName: item.portClusterInitialPortName,
            berthName: item.berthName,
        };
    };

    return {
        newEventList,
        addOfflineDataToEventList,
        getEventAnyFromTanksEventData,
        getLastUpdatedForEventListAnyOnly,
        newTankersEvent,
        newItem,
        createNewEventFromCode,
        createNewEventList,
        createNewEvent,
        createSuperShallowEvent,
        createTankersEventBM,
    };
};
